import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  ButtonContainer,
  Figcaption,
  Figure,
  Form,
  FormContainer,
  FormGroup,
  FormGroupContainer,
  Input,
  Label,
  SaveButton,
  Select,
  TextInput,
  Thumbnail,
  UploadButton,
  UploadIcon,
  UploadInput,
  UploadWrap,
} from "./form.style";
import { firebaseDB } from "../firebaseConfig";
import { set, ref, get } from "firebase/database";
import { firebaseStorage } from "../firebaseConfig";
import {
  ref as stRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import TagInput from "./tag-input.component";

const db = firebaseDB;
const storage = firebaseStorage;

const ArticleForm = ({
  magazine,
  editingArticle,
  setEditingArticle,
  newestArticleID,
}) => {
  // Publisher
  const publisher = {
    logo: {
      url: "https://katapult-magazin.de/assets/img/katapult-logo-dark.svg",
    },
    name: "Katapult Verlag",
  };

  // Content-Objekt
  const [contents, setContents] = useState([]);
  const [contentId, setContentId] = useState(1);

  // Autoren-Objekt
  const [authors, setAuthors] = useState([{ name: "", url: "" }]);

  // Artikel-Objekt
  const [articleId, setArticleId] = useState(0);
  const [articleUrl, setArticleUrl] = useState("");
  const [articleTopic, setArticleTopic] = useState("");
  const [articleHeadline, setArticleHeadline] = useState("");
  const [articleDescription, setArticleDescription] = useState("");
  const [ytLinkID, setYtLinkID] = useState("");
  const [datePublished, setDatePublished] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const fileInputRef = useRef(null);

  // Keywords
  const [tags, setTags] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const addAuthor = () => {
    setAuthors([...authors, { name: "", url: "" }]);
  };

  const updateAuthor = (index, field) => (e) => {
    const newAuthors = [...authors];
    newAuthors[index][field] = e.target.value;
    setAuthors(newAuthors);
  };

  const addContent = () => {
    setContents((prevContents) => [
      ...prevContents,
      {
        id: contentId.toString(),
        type: "",
        headline: "",
        text: "",
        url: "",
        figcaption: "",
      },
    ]);
    setContentId((prevContentId) => prevContentId + 1);
  };

  const updateContent = (index, field) => (e) => {
    const newContents = [...contents];
    newContents[index][field] = e.target.value;
    setContents(newContents);
  };

  const updateImageContent = (index, field) => (url) => {
    const newContents = [...contents];
    newContents[index][field] = url;
    setContents(newContents);
  };

  const handleArticleIdChange = (e) => {
    setArticleId(e.target.value);
  };

  const handleArticleUrlChange = (e) => {
    setArticleUrl(e.target.value);
  };

  const handleImageUpload = (index) => async () => {
    if (fileInputRef.current.files.length === 0) {
      alert(
        "Bitte wählen Sie zuerst eine Datei aus, bevor Sie auf 'Upload' klicken.",
      );
      return;
    }

    if (fileInputRef.current.files.length > 0) {
      const file = fileInputRef.current.files[0];
      const storageRef = stRef(
        storage,
        `articles/${magazine}/${articleId}/content/${file.name}`,
      );
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Setzen Sie den Status auf 'uploading', wenn der Upload beginnt
      setUploadStatus("uploading");

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Progress function
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
        },
        (error) => {
          // Error function
          console.log(error);
          setUploadStatus("error");
        },
        () => {
          // Complete function
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            console.log("File available at", downloadURL);
            if (contents[index]) {
              updateImageContent(index, "url")(downloadURL);
            } else {
              console.log(
                `Es existiert kein Inhaltselement an der Indexposition ${index}.`,
              );
            }
            setUploadStatus("success");
          });
        },
      );
    } else {
      console.log("kein Bild ausgewählt!");
    }
  };

  const handleDatePublishedChange = (e) => {
    setDatePublished(e.target.value);
  };

  const handleArticleTopicChange = (e) => {
    setArticleTopic(e.target.value);
  };

  const handleArticleHeadlineChange = (e) => {
    setArticleHeadline(e.target.value);
  };

  const handleArticleDescriptionChange = (e) => {
    setArticleDescription(e.target.value);
  };

  const handleArticleYtIdChange = (e) => {
    setYtLinkID(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Magazin-Auswahl und Artikel-ID bestätigen
    const confirm = window.confirm(
      "Soll der Artikel in der " + magazine + " Datenbank gespeichert werden?",
    );

    // Hier fügst du die Logik hinzu, um den Inhalt in der Firebase Realtime Database zu speichern
    if (confirm) {
      try {
        if (contents.length > 0) {
          await set(ref(db, `articles/${magazine}/${articleId}`), {
            id: articleId.toString(),
            publisher: publisher,
            magazine: magazine,
            url: articleUrl,
            author: authors,
            topic: articleTopic,
            headline: articleHeadline,
            description: articleDescription,
            content: contents,
            dateModified: new Date().toISOString().split("T")[0],
            datePublished: datePublished,
            ytLinkID: ytLinkID ? ytLinkID : null,
          });

          // Keyword-Logik
          const keywordsRef = ref(db, `keywords/${magazine}`);
          const snapshot = await get(keywordsRef);
          const keywordsData = snapshot.exists() ? snapshot.val() : {};

          // Aktualisiere die Keywords in der Firebase-Datenbank
          const currentKeywords = new Set(tags); // Annahme: 'tags' enthält die aktuellen Keywords des Artikels
          const originalKeywords = new Set(Object.keys(keywordsData)); // Annahme: Du hast irgendwo die ursprünglichen Keywords gespeichert

          // Füge neue Keywords hinzu oder aktualisiere bestehende
          for (const keyword of currentKeywords) {
            const existingIds = keywordsData[keyword] || [];
            if (!existingIds.includes(articleId)) {
              const updatedIds = [...existingIds, articleId.toString()];
              await set(ref(db, `keywords/${magazine}/${keyword}`), updatedIds);
            }
          }

          // Entferne die Artikel-ID von den gelöschten Keywords
          for (const keyword of originalKeywords) {
            if (!currentKeywords.has(keyword)) {
              const updatedArticleIds = keywordsData[keyword].filter(
                (id) => id !== articleId.toString(),
              );
              await set(
                ref(db, `keywords/${magazine}/${keyword}`),
                updatedArticleIds,
              );
            }
          }
          clearForm();
          setEditingArticle("");
        } else {
          alert("Bitte füge Inhalte hinzu, bevor du den Artikel speicherst.");
        }
      } catch (e) {
        alert("Ein Fehler ist aufgetreten: " + e.message);
      } finally {
        alert("Artikel erfolgreich gespeichert");
      }
    }
  };

  const clearForm = () => {
    // Zurücksetzen der Formularfelder
    setArticleId(newestArticleID);
    setArticleUrl("");
    setDatePublished("");
    setAuthors([{ name: "", url: "" }]);
    setContents([]);
    // setContentId(prevState => prevState + 1);
    setArticleTopic("");
    setArticleHeadline("");
    setArticleDescription("");
    setTags([]);
    setYtLinkID("");
  };

  const resetForm = () => {
    const confirm = window.confirm("Sollen alle Felder zurückgesetzt werden?");

    if (confirm) {
      clearForm();
    }
  };

  useEffect(() => {
    setArticleId(newestArticleID);
  }, [newestArticleID]);

  useEffect(() => {
    if (editingArticle) {
      clearForm();
      setArticleId(editingArticle.id);
      setArticleUrl(editingArticle.url);
      setArticleTopic(editingArticle.topic);
      setArticleHeadline(editingArticle.headline);
      setArticleDescription(editingArticle.description);
      setDatePublished(editingArticle.datePublished);
      setContents(editingArticle.content || []);
      setAuthors(editingArticle.author || [{ name: "", url: "" }]);
      setYtLinkID(editingArticle.ytLinkID);
    } else {
      clearForm();
    }
  }, [editingArticle]);

  useEffect(() => {
    loadKeywordsForArticle().catch((e) => console.log(e));
  }, [articleId]);

  const loadKeywordsForArticle = async () => {
    if (articleId) {
      const keywordsRef = ref(db, `keywords/${magazine}`);
      const snapshot = await get(keywordsRef);
      if (snapshot.exists()) {
        const keywordsData = snapshot.val();
        const associatedKeywords = Object.keys(keywordsData).filter((keyword) =>
          keywordsData[keyword].includes(articleId),
        );
        setTags(associatedKeywords);
        setSuggestions(Object.keys(keywordsData));
      } else {
        setTags([]);
        setSuggestions([]);
      }
    } else {
      setTags([]);
    }
  };

  return (
    <FormContainer>
      <h2 style={{ margin: "0 0 .75em 0" }}>Artikel erstellen/bearbeiten</h2>
      <Form onSubmit={handleSubmit} onReset={resetForm}>
        <FormGroupContainer>
          {/* Artikel-ID */}
          <FormGroup>
            <Label htmlFor="article-id">ID</Label>
            <Input
              type="number"
              id="article-id"
              name="article-id"
              placeholder="000"
              value={articleId}
              onChange={handleArticleIdChange}
              required
            />
          </FormGroup>

          {/* Date Published*/}
          <FormGroup>
            <Label htmlFor="date-published">Veröffentlichung</Label>
            <Input
              type="date"
              id="date-published"
              name="date-published"
              value={datePublished}
              onChange={handleDatePublishedChange}
              required
            />
          </FormGroup>
        </FormGroupContainer>

        {/* Artikel-URL */}
        <FormGroup>
          <Label htmlFor="article-url">Webseite URL</Label>
          <Input
            type="url"
            id="article-url"
            name="article-url"
            placeholder="https://katapult-magazin.de/de/artikel/scholz-vs-letzte-generation"
            value={articleUrl}
            onChange={handleArticleUrlChange}
            required
          />
        </FormGroup>

        {/* Topic */}
        <FormGroup>
          <Label htmlFor="article-topic">Thema</Label>
          <Input
            type="text"
            id="article-topic"
            name="article-topic"
            placeholder="Klimaschutz"
            value={articleTopic}
            onChange={handleArticleTopicChange}
            required
          />
        </FormGroup>

        {/* Titel */}
        <FormGroup>
          <Label htmlFor="article-title">Titel</Label>
          <Input
            type="text"
            id="article-title"
            name="article-title"
            placeholder="Scholz vs. Letzte Generation"
            value={articleHeadline}
            onChange={handleArticleHeadlineChange}
            required
          />
        </FormGroup>

        {/* Beschreibung */}
        <FormGroup>
          <Label htmlFor="article-description">Beschreibung</Label>
          <TextInput
            type="text"
            rows="5"
            cols="33"
            id="article-description"
            name="article-description"
            placeholder="In einem Gespräch mit Schüler:innen hat Olaf Scholz die Aktionen der Letzten Generation als „völlig bekloppt“ bezeichnet..."
            value={articleDescription}
            onChange={handleArticleDescriptionChange}
            required
          />
        </FormGroup>

        {/* Autoren */}
        <h3>Autor:innen</h3>
        {authors.map((_, index) => (
          <React.Fragment key={index}>
            <FormGroup>
              <Label htmlFor={`author-name-${index}`}>Vor- und Nachname</Label>
              <Input
                type="text"
                id={`author-name-${index}`}
                name={`author-name-${index}`}
                placeholder="Redaktion"
                value={authors[index].name}
                onChange={updateAuthor(index, "name")}
                required
              />
              <Label htmlFor={`author-url-${index}`}>URL</Label>
              <Input
                type="url"
                id={`author-url-${index}`}
                name={`author-url-${index}`}
                placeholder="https://katapult-magazin.de/de/autoren/redaktion"
                value={authors[index].url}
                onChange={updateAuthor(index, "url")}
                required
              />
              <ButtonContainer>
                <Button type="button" onClick={addAuthor}>
                  Weiteren Autor hinzufügen
                </Button>
              </ButtonContainer>
            </FormGroup>
          </React.Fragment>
        ))}

        {/* Inhalte */}
        <h3>Inhalte</h3>
        {contents.map((_, index) => (
          <React.Fragment key={index}>
            <FormGroup>
              <Label htmlFor={`content-type-${index}`}>Inhaltstyp</Label>
              <Select
                id={`content-type-${index}`}
                name={`content-type-${index}`}
                onChange={updateContent(index, "type")}
                value={contents[index].type}
                required
              >
                <option>-- Inhaltstyp auswählen --</option>
                <option value="headline">Überschrift</option>
                <option value="text">Text</option>
                <option value="cite">Zitat</option>
                <option value="image">Bild</option>
                <option value="figcaption">Bildbeschreibung</option>
              </Select>
            </FormGroup>
            {contents[index].type === "headline" && (
              <FormGroup>
                <Label htmlFor={`content-headline-${index}`}>Überschrift</Label>
                <Input
                  type="text"
                  id={`content-headline-${index}`}
                  name={`content-headline-${index}`}
                  placeholder="Überschrift"
                  onChange={updateContent(index, "headline")}
                  value={contents[index].headline}
                />
              </FormGroup>
            )}
            {contents[index].type === "text" && (
              <FormGroup>
                <Label htmlFor={`content-text-${index}`}>Textabschnitt</Label>
                <TextInput
                  type="text"
                  rows="5"
                  cols="33"
                  id={`content-text-${index}`}
                  name={`content-text-${index}`}
                  placeholder="Über unterschiedliche Protestformen kann ohne Zweifel..."
                  onChange={updateContent(index, "text")}
                  value={contents[index].text}
                />
              </FormGroup>
            )}
            {contents[index].type === "cite" && (
              <FormGroup>
                <Label htmlFor={`content-text-${index}`}>Zitat</Label>
                <Input
                  type="text"
                  id={`content-text-${index}`}
                  name={`content-text-${index}`}
                  placeholder="..."
                  onChange={updateContent(index, "text")}
                  value={contents[index].text}
                />
              </FormGroup>
            )}
            {contents[index].type === "image" && (
              <FormGroup>
                <Label>Vorschau</Label>
                <Figure>
                  <Thumbnail
                    async
                    src={contents[index].url}
                    alt={contents[index].url}
                  />
                  <Figcaption>{contents[index].url}</Figcaption>
                </Figure>
                <Label htmlFor={`content-file-${index}`}>Bild hochladen</Label>
                <UploadWrap>
                  <UploadInput
                    type="file"
                    id={`content-file-${index}`}
                    name={`content-file-${index}`}
                    accept="image/*"
                    onChange={updateContent(index, "url")}
                    ref={fileInputRef}
                  />
                  <UploadButton
                    type="button"
                    onClick={handleImageUpload(index)}
                  >
                    Upload
                    <UploadIcon src="/uploadImage.png" alt="Upload" />
                  </UploadButton>
                </UploadWrap>
                <p>
                  Bitte klicke auf "Upload", nachdem eine Datei ausgewählt
                  wurde.
                </p>
                {uploadStatus === "uploading" && <p>Wird hochgeladen...</p>}
                {uploadStatus === "success" && <p>Erfolgreich hochgeladen!</p>}
                {uploadStatus === "error" && <p>Fehler beim Hochladen.</p>}
              </FormGroup>
            )}
            {contents[index].type === "figcaption" && (
              <FormGroup>
                <Label htmlFor={`content-figcaption-${index}`}>
                  Bildbeschreibung
                </Label>
                <Input
                  type="text"
                  id={`content-figcaption-${index}`}
                  name={`content-figcaption-${index}`}
                  placeholder="Bildbeschreibung"
                  onChange={updateContent(index, "figcaption")}
                  value={contents[index].ficaption}
                />
              </FormGroup>
            )}
          </React.Fragment>
        ))}
        <ButtonContainer>
          {contents.length > 0 ? (
            <Button type="button" onClick={addContent}>
              Weiteren Inhalt hinzufügen
            </Button>
          ) : (
            <Button type="button" onClick={addContent}>
              Inhalt hinzufügen
            </Button>
          )}
        </ButtonContainer>

        {/* Keywords */}
        <h3>Keywords</h3>
        <FormGroup>
          <Label htmlFor="keywords">Schlagwörter für Suche</Label>
          <TagInput
            tags={tags}
            setTags={setTags}
            suggestions={suggestions}
            setSuggestions={setSuggestions}
          />
        </FormGroup>

        <h3>Youtube</h3>
        {/* Youtube */}
        <FormGroup>
          <Label htmlFor="article-youtube">Video ID </Label>
          <Input
            type="text"
            id="article-youtube"
            name="article-youtube"
            placeholder="Füge die ID ein z.B.: Y3goj1z9JYM"
            value={ytLinkID}
            onChange={handleArticleYtIdChange}
          />
        </FormGroup>

        {/* Speichern-Button */}
        <ButtonContainer>
          <Button type="reset">Zurücksetzen</Button>
          <SaveButton
            type="submit"
            disabled={
              uploadStatus !== "success" &&
              !magazine &&
              magazine !== "-- Magazin auswählen --"
            }
          >
            Speichern
          </SaveButton>
        </ButtonContainer>
      </Form>
    </FormContainer>
  );
};

export default ArticleForm;
